// CTV-800: the generation ledger, shared by the buyer surfaces (WaymarkPanel,
// wizard copy) and the staff Studio (StudioPage, staff copy). Extracted from
// WaymarkPanel.tsx unchanged in markup and test ids; only the row copy became
// a prop, so both surfaces render the same four-stage timeline and the same
// markers. `StagePills` is the neutral (light background) rendering of the
// disclosure banner's four-segment stage indicator, for surfaces without the
// station gradient.

/* eslint-disable react-refresh/only-export-components -- ledger.tsx exports the ledger's pure helpers (formatClock, ledgerStageFor) beside its components, matching WaymarkDisclosure */
import React from 'react';
import { Check } from 'lucide-react';
import { tintFromAccent } from '../steps/_shared';
import type { WaymarkStage } from './context';

/** The four rows of the timeline; `ready` collapses the whole thing. */
export type LedgerRowKey = 'analyzing' | 'drafting' | 'customize' | 'finalizing';

export interface LedgerRowSpec {
  key: LedgerRowKey;
  doneLabel: (host: string) => string;
  /** The stage whose observed entry marks this row complete (its clock time). */
  completedAtStage: WaymarkStage;
  currentTitle: (host: string) => string;
  currentDesc: (host: string, priorCount: number) => string | null;
  /** Right-hand hint on the current row. Total remaining, never the current
      stage's own estimate (the banner already shows that). */
  currentHint: string | null;
  todoLabel: string;
  todoHint: string | null;
}

export function formatClock(ms: number | undefined): string | null {
  if (ms == null) return null;
  return new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/**
 * The coarse ledger stage of a commercial from its provider state. Mirrors
 * the derivation WaymarkPanel makes inline; the Studio reads the same rule.
 */
export function ledgerStageFor(input: {
  status: string;
  isFinalizing: boolean;
  renderedUrl: string | null;
}): WaymarkStage | null {
  if (input.isFinalizing) return 'finalizing';
  switch (input.status) {
    case 'topic_searching':
      return 'analyzing';
    case 'generating':
      return 'drafting';
    case 'ready':
      return input.renderedUrl ? 'ready' : 'customize';
    default:
      return null;
  }
}

export const LedgerTimeline: React.FC<{
  accentColor: string;
  rows: LedgerRowSpec[];
  stage: LedgerRowKey;
  host: string;
  priorCount: number;
  stageEnteredAt: Partial<Record<WaymarkStage, number>>;
}> = ({ accentColor, rows, stage, host, priorCount, stageEnteredAt }) => {
  const activeIdx = rows.findIndex((r) => r.key === stage);
  return (
    <div className="relative" data-testid="waymark-ledger-rows">
      <span className="absolute left-[8px] top-3 bottom-3 w-[2px] bg-slate-100" aria-hidden />
      {rows.map((row, i) => {
        if (i < activeIdx) {
          const time = formatClock(stageEnteredAt[row.completedAtStage]);
          return (
            <div
              key={row.key}
              className="relative flex items-start gap-2.5 py-1"
              data-testid="waymark-ledger-row"
              data-state="done"
              data-key={row.key}
            >
              <LedgerMarker kind="done" accentColor={accentColor} />
              <span className="flex-1 min-w-0 text-[12.5px] font-semibold text-slate-900">
                {row.doneLabel(host)}
              </span>
              {time && (
                <span className="ml-auto pl-2.5 pt-0.5 text-[10.5px] text-slate-400 tabular-nums whitespace-nowrap">
                  {time}
                </span>
              )}
            </div>
          );
        }
        if (i === activeIdx) {
          return (
            <div
              key={row.key}
              className="relative flex items-start gap-2.5 -mx-2 my-0.5 px-2 py-2 rounded-[10px]"
              data-testid="waymark-ledger-row"
              data-state="current"
              data-key={row.key}
              style={{ backgroundColor: tintFromAccent(accentColor, 0.06) }}
            >
              <LedgerMarker kind="current" accentColor={accentColor} />
              <span className="flex-1 min-w-0">
                <span className="block text-[12.5px] font-semibold text-slate-900">
                  {row.currentTitle(host)}
                </span>
                {row.currentDesc(host, priorCount) && (
                  <span className="block text-[11.5px] text-slate-500 leading-snug mt-px">
                    {row.currentDesc(host, priorCount)}
                  </span>
                )}
              </span>
              {row.currentHint && (
                <span className="ml-auto pl-2.5 pt-0.5 text-[10.5px] text-slate-400 whitespace-nowrap">
                  {row.currentHint}
                </span>
              )}
            </div>
          );
        }
        return (
          <div
            key={row.key}
            className="relative flex items-start gap-2.5 py-1"
            data-testid="waymark-ledger-row"
            data-state="todo"
            data-key={row.key}
          >
            <LedgerMarker kind="todo" accentColor={accentColor} />
            <span className="flex-1 min-w-0 text-[12.5px] font-medium text-slate-400">
              {row.todoLabel}
            </span>
            {row.todoHint && (
              <span className="ml-auto pl-2.5 pt-0.5 text-[10.5px] text-slate-400 whitespace-nowrap">
                {row.todoHint}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};

export const LedgerMarker: React.FC<{ kind: 'done' | 'current' | 'todo'; accentColor: string }> = ({
  kind,
  accentColor,
}) => {
  if (kind === 'done') {
    return (
      <span
        className="relative z-[1] mt-px w-[18px] h-[18px] rounded-full flex items-center justify-center shrink-0 text-white"
        style={{ backgroundColor: accentColor }}
        aria-hidden
      >
        <Check className="w-3 h-3" strokeWidth={3} />
      </span>
    );
  }
  if (kind === 'current') {
    return (
      <span
        className="relative z-[1] mt-px w-[18px] h-[18px] rounded-full flex items-center justify-center shrink-0 border-2 bg-white"
        style={{ borderColor: accentColor, backgroundColor: tintFromAccent(accentColor, 0.1) }}
        aria-hidden
      >
        <span
          className="w-1.5 h-1.5 rounded-full motion-safe:animate-pulse"
          style={{ backgroundColor: accentColor }}
        />
      </span>
    );
  }
  return (
    <span
      className="relative z-[1] mt-px w-[18px] h-[18px] rounded-full shrink-0 border-2 border-slate-200 bg-white"
      aria-hidden
    />
  );
};

/** One pill per stage: the word beside the segments and the time hint. */
export interface StagePillSpec {
  stage: 1 | 2 | 3 | 4;
  label: string;
  estimate: string | null;
  /** Animates the current segment during a wait stage. */
  pulse: boolean;
  /** Every segment reads done (the run is over). */
  done?: boolean;
}

/**
 * The disclosure banner's four-segment stage indicator, on a light
 * background: same 18x6 segments, same `data-state` per segment, same
 * `Stage N of 4` label, with the accent standing in for the banner's white.
 */
export const StagePills: React.FC<{
  accentColor: string;
  spec: StagePillSpec;
  className?: string;
}> = ({ accentColor, spec, className }) => (
  <span
    className={`flex items-center gap-2 ${className ?? ''}`}
    data-testid="waymark-stage-pills"
    data-stage={spec.stage}
    aria-label={`Stage ${spec.stage} of 4: ${spec.label}`}
  >
    <span className="flex items-center gap-1" aria-hidden>
      {([1, 2, 3, 4] as const).map((n) => {
        const segState =
          spec.done || n < spec.stage
            ? 'done'
            : n === spec.stage
              ? spec.pulse
                ? 'active'
                : 'current'
              : 'pending';
        return (
          <span
            key={n}
            className={`block w-[18px] h-[6px] rounded-full ${
              segState === 'active' ? 'motion-safe:animate-pulse' : ''
            }`}
            data-state={segState}
            style={{
              background: segState === 'pending' ? tintFromAccent(accentColor, 0.18) : accentColor,
              opacity: segState === 'current' ? 0.85 : 1,
            }}
          />
        );
      })}
    </span>
    <span className="text-[12px] font-semibold text-slate-800" data-testid="waymark-stage-pills-label">
      {spec.label}
      {spec.estimate && <span className="font-medium text-slate-500"> · {spec.estimate}</span>}
    </span>
  </span>
);
